<template>
  <nav ref="navigation" class="navigation-rail" data-testid="navigation-rail" @mouseleave="close">
    <ul id="nav-top-menu" class="grid-container grid-x align-justify">
      <li v-if="killswitches.enhancedDesktopNavEnabled" class="fob-item hamburger-menu" :class="{ active: isMenuOpened }" @mouseover="closeFlyouts">
        <button
          ref="menuTarget"
          aria-label="Open Shop All Menu"
          aria-haspopup="true"
          aria-controls="mobile-nav"
          :aria-expanded="isMenuOpened"
          data-testid="burger-button"
          data-block-outside-click
          class="hamburger-menu-btn grid-x padding-bottom-xs"
          @click="toggleNav"
        >
          <span class="hamburger-menu-icon grid-y" :class="{ 'hamburger-menu-icon--open': isMenuOpened }">
            <span></span>
            <span></span>
            <span></span>
          </span>
          <span class="fob-text menu-label" :class="fobClass">Shop All</span>
        </button>
      </li>
      <li
        v-for="(fob, index) in fobs"
        :key="fob.id"
        class="fob-item"
        :class="{ active: isActive(fob.text), 'padding-bottom-xs': !killswitches.enhancedDesktopNavEnabled, 'menu-opened': isMenuOpened }"
        @mouseover="onMouseOver(fob)"
        @mouseleave="onMouseLeave"
        @focusin="onFocusIn"
        @focusout="clearFocusInTimer"
      >
        <Link
          :id="`fob-${fob.text}`"
          :href="fob.url"
          :data-id="fob.id"
          :data-text="fob.text"
          :data-tracking="trackingObjToString(fob.tracking)"
          class="fob-text"
          :class="fobClass"
          :style="fob.color && { color: fob.color }"
          @click="onFlyoutLinkClick($event, fob, index)"
          @keydown.arrow-left.prevent="onKeydownArrowLeft(navigation)"
          @keydown.arrow-right.prevent="onKeydownArrowRight(navigation)"
        >
          <ToysIcon v-if="isMcom && isToysCategory({ id: getCategoryId(fob), text: fob.text })" />
          <span v-else>{{ fob.text }}</span>
        </Link>

        <button
          class="chevron-button"
          :class="{ active: isActive(fob.text) }"
          :aria-expanded="isActive(fob.text)"
          :aria-controls="fob.text"
          :aria-label="`Open ${fob.text} menu`"
          @keydown.tab="close"
          @keydown.esc.prevent="close"
          @keydown.arrow-up.prevent="close"
          @keydown.enter.prevent="onToggleVisibility(fob)"
          @keydown.space.prevent="onToggleVisibility(fob)"
          @keydown.arrow-down.prevent="onToggleVisibility(fob)"
          @keydown.arrow-left.prevent="onKeydownArrowLeft(navigation)"
          @keydown.arrow-right.prevent="onKeydownArrowRight(navigation)"
        >
          <span class="chevron-small-down"></span>
        </button>
      </li>
    </ul>
    <Flyouts :flyout-category-name="activeFobId" :fobs="fobs" @close="closeAndMoveFocusToCurrentFob" />
    <div
      v-if="killswitches.enhancedDesktopNavEnabled"
      class="enhanced-nav-wrapper grid-container grid-x align-justify"
      :class="{ active: isMenuOpened }"
      :style="isMenuOpened ? contentWrapStyles : ''"
    >
      <MobileMenu />
    </div>
  </nav>
</template>
<script setup>
/* v8 ignore file -- @preserve */
import { computed, watch, ref } from 'vue';

import Link from '~/components/common/Link';
import Flyouts from '~/components/header-desktop/Flyouts.common';
import MobileMenu from '~/components/header-mobile/MobileMenu.common';
import ToysIcon from '~/components/dumb/ToysIcon.mcom';
import { useDesktopNavigation } from '~/composables/accessibility/useDesktopNavigation.js';
import { useOverlay } from '~/composables/ui/useOverlay.js';
import { tagNavLinkClick } from '#shared/features/analytics/navigationTags';
import { useFlyoutsMenu } from '~/composables/useFlyoutsMenu';
import { trackingObjToString } from '~/utils/restoreStateFromDom.js';
import { useMobileNav } from '~/composables/useMobileNav.js';
import isToysCategory from '~/utils/isToysCategory';
import getCategoryId from '~/utils/getCategoryId';
import useKillswitches from '~/composables/useKillswitches';

const navigation = ref(null);

const envProps = useEnvProps();
const pageData = usePageData();

const isMcom = envProps.isMcom;
const overlay = useOverlay('NAVIGATION_OVERLAY');
const { activeFobId, open, close, isActive, onToggleVisibility, onKeydownArrowLeft, onKeydownArrowRight, moveFocusToCurrentFob } =
  useDesktopNavigation();
const { flyouts: fobs } = useFlyoutsMenu();
const killswitches = useKillswitches();

const { context } = pageData.navigation;
const { menuTarget, isMenuOpened, toggleNav, closeNav } = useMobileNav();
const fobClass = computed(() => (fobs.value.length > 12 ? 'fob-small' : ''));
const hoveredFobId = ref();
const contentWrapStyles = ref();
const isFOBTriggerFocusOn = ref(false);
const FLYOUT_TIMER = 200;
const onFocusInTimer = ref(null);
const onMouseOverTimer = ref(null);

function closeAndMoveFocusToCurrentFob() {
  moveFocusToCurrentFob();
  close();
}

/**
 * A, AAA Complaint #PLP1-2645
 * Help prevent FOB flyout from triggering automatically while voice
 * over/reader is active. Applies to users with "Follow VoiceOver Cursor" enabled
 * in their system through VoiceOver Utility > Navigation preference.
 */
function onFocusIn() {
  isFOBTriggerFocusOn.value = true;
  onFocusInTimer.value = setTimeout(() => {
    isFOBTriggerFocusOn.value = false;
  }, FLYOUT_TIMER + 200); // should always be longer than onMouseOver() timeout
}

function clearFocusInTimer() {
  clearTimeout(onFocusInTimer.value);
  onFocusInTimer.value = null;
}

function clearMouseOverTimer() {
  clearTimeout(onMouseOverTimer.value);
  onMouseOverTimer.value = null;
}

function onMouseOver(fob) {
  hoveredFobId.value = fob.id;

  onMouseOverTimer.value = setTimeout(() => {
    if (hoveredFobId.value === fob.id && !isFOBTriggerFocusOn.value) {
      open(fob);
    }
  }, FLYOUT_TIMER);
}

function onFlyoutLinkClick(event, fob, index) {
  if (isMenuOpened.value) {
    event.preventDefault();
    closeNav();
    onMouseOver(fob);
    return;
  }

  tagNavLinkClick(isMcom, {
    categoryId: fob.id,
    clickableElement: fob.text,
    col: 1,
    pos: index + 1,
    context,
    tracking: fob.tracking,
    topCategoryName: fob.text,
  });
}

function onMouseLeave() {
  hoveredFobId.value = null;
  clearMouseOverTimer();
}

function closeFlyouts() {
  onMouseLeave();
  close();
}

watch(isMenuOpened, (value) => {
  if (value) {
    // some 3rd-party scripts can change height of the header, so calculate it only when the menu is opened
    contentWrapStyles.value = { height: `calc(100vh - ${document.getElementById('nav-header-root').offsetHeight}px` };
  }
});

watch(activeFobId, (value) => {
  if (value) {
    overlay.show();
  } else {
    overlay.hide();
  }
});
</script>

<style lang="scss" scoped>
@import '~/assets/scss/mixins/index.scss';

.enhanced-nav-wrapper {
  position: relative;
  overflow: hidden;

  &.active {
    z-index: auto;
    overflow: visible;

    &::before {
      content: '';
      display: block;
      position: absolute;
      left: -100%;
      top: 1px;
      height: 100vh;
      width: 100%;
      background-color: $white;
      z-index: 100;
    }
  }
}

.navigation-rail {
  position: relative;
  border-bottom: 1px solid $gray-4-color;
}

.fob-text {
  display: block;
  border: none;
  text-decoration: none;
  font-weight: 700;

  &:hover {
    text-decoration: none;
  }
}

.menu-opened {
  opacity: 0.3;
  pointer-events: none;
}

.fob-item {
  position: relative;
  @include padding-top-xs;

  &:hover,
  &:focus,
  &.active {
    &::after {
      position: absolute;
      content: '';
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background-color: $black;
    }
  }
}

.chevron-button {
  position: absolute;
  bottom: 0;
  height: 16px;
  left: 0;
  width: 100%;
  opacity: 0;
  pointer-events: none;
  transition: transform 0.2s ease-in-out;

  &:focus {
    opacity: 1;
    pointer-events: auto;
  }

  &.active {
    .chevron-small-down {
      transform: rotate(180deg);
    }
  }

  .chevron-small-down {
    position: absolute;
    left: calc(50% - 12px);
  }
}

.hamburger-menu-btn {
  cursor: pointer;
  align-items: center;

  .menu-label {
    margin-left: 12px;
  }
}

.hamburger-menu-icon {
  position: relative;
  width: 16px;
  height: 16px;
  align-items: center;

  span {
    background-color: #000;
    display: block;
    height: 2px;
    width: 15px;
    border-radius: 2px;
    margin-top: 3px;
    transition: all 120ms ease-in;
  }

  span:nth-child(1) {
    margin-top: 1px;
  }

  &--open {
    span:nth-child(1) {
      transform: translateY(5px) rotate(45deg);
    }

    span:nth-child(2) {
      opacity: 0;
    }

    span:nth-child(3) {
      transform: translateY(-5px) rotate(-45deg);
    }
  }
}
</style>

<style lang="scss" scoped brand="mcom">
@import '~/assets/scss/icons.mcom.scss';

.fob-text {
  font-size: 14px;
  line-height: 20px;
}

// prevent links overlapping for small desktop screens
@media only screen and (max-width: 1120px) {
  .fob-small {
    font-size: 12px;
  }
}

.navigation-rail {
  height: 53px;
}

.chevron-button {
  height: 14px;

  .chevron-small-down {
    @extend .chevron-small-down-black;
    top: -5px;
  }
}

.hamburger-menu-icon {
  top: 1px;
}
</style>

<style lang="scss" scoped brand="bcom">
@import '~/assets/scss/icons.bcom.scss';

.navigation-rail {
  height: 49px;
}

.chevron-button {
  height: 16px;

  .chevron-small-down {
    @extend .chevron-small-down;
    top: -4px;
  }
}

.fob-text {
  font-size: 12px;
  line-height: 16px;
}

.hamburger-menu-btn {
  .menu-label {
    text-transform: uppercase;
  }
}
</style>
